import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import { getSegregateTrades, getStrategies, segregateTrades } from "../../api/strategyProfiling";
import {
  extractSegregateTradesList,
  filterSegregateModalTrades,
  normalizeSegregateModalTrades,
} from "./segregateTradesModalUtils";
import styles from "./SegregateTradesModal.module.css";

const getErrorMessage = (err) => err?.response?.data?.detail || err.message || "An error occurred";

const SelectDropdownIcon = (props) => (
  <GlobalIcon iconName="SelectDropdownArrow" className={styles["select-icon"]} {...props} />
);

const EXCHANGE_OPTIONS = ["NSE", "BSE", "MCX"];
const INSTRUMENT_OPTIONS = ["FnO", "EQ", "COMM"];

const DEFAULT_FILTERS = {
  symbol: "all",
  startDate: "",
  expiryDate: "",
  instrumentType: "all",
  brokerCode: "all",
  strategy: "",
  strike: "",
  batchId: "",
};

function SegregateTradesModal({
  open,
  onClose,
  onSuccess,
  initialSelectedIds = [],
  initialBatchId = "",
}) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [transactionType, setTransactionType] = useState("Buy");
  const [trades, setTrades] = useState([]);
  const [selectedTradeIds, setSelectedTradeIds] = useState([]);
  const [strategyOptions, setStrategyOptions] = useState([]);
  const [isLoadingTrades, setIsLoadingTrades] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setError("");
    setIsLoadingTrades(true);
    setFilters({
      ...DEFAULT_FILTERS,
      batchId: initialBatchId || "",
    });
    setTransactionType("Buy");
    setSelectedTradeIds(initialSelectedIds);

    const loadData = async () => {
      try {
        const [tradesResponse, strategiesResponse] = await Promise.all([
          getSegregateTrades(),
          getStrategies(),
        ]);
        if (cancelled) return;

        const rawTrades = extractSegregateTradesList(tradesResponse);
        setTrades(normalizeSegregateModalTrades(rawTrades));

        const strategies = Array.isArray(strategiesResponse)
          ? strategiesResponse
          : strategiesResponse?.data || [];
        setStrategyOptions(strategies);
        if (strategies.length > 0) {
          setFilters((prev) => ({
            ...prev,
            batchId: initialBatchId || prev.batchId,
            strategy: prev.strategy || strategies[0].strategy_name || "",
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(getErrorMessage(err));
          setTrades([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingTrades(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [initialBatchId, initialSelectedIds, open]);

  const filteredTrades = useMemo(
    () =>
      filterSegregateModalTrades(trades, {
        ...filters,
        transactionType,
      }),
    [filters, trades, transactionType]
  );

  const allVisibleSelected =
    filteredTrades.length > 0 &&
    filteredTrades.every((trade) => selectedTradeIds.includes(trade.id));

  const someVisibleSelected = filteredTrades.some((trade) =>
    selectedTradeIds.includes(trade.id)
  );

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (error) setError("");
  }, [error]);

  const handleToggleRow = useCallback((tradeId) => {
    setSelectedTradeIds((prev) =>
      prev.includes(tradeId) ? prev.filter((id) => id !== tradeId) : [...prev, tradeId]
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    if (allVisibleSelected) {
      const visibleIds = new Set(filteredTrades.map((trade) => trade.id));
      setSelectedTradeIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }

    const visibleIds = filteredTrades.map((trade) => trade.id);
    setSelectedTradeIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  }, [allVisibleSelected, filteredTrades]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const handleSubmit = useCallback(async () => {
    if (selectedTradeIds.length === 0) {
      setError("Please select at least one trade to segregate.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await segregateTrades(selectedTradeIds);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [onClose, onSuccess, selectedTradeIds]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ className: styles["dialog-paper"] }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(15, 23, 42, 0.4)" },
        },
      }}
    >
      <div className={styles["modal-content"]}>
        <h2 className={styles["modal-title"]}>Segregate Trades</h2>

        <div className={styles["filters-grid"]}>
          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-symbol">
              Symbol Selection
            </label>
            <FormControl size="small" fullWidth className={styles["field-input"]}>
              <Select
                id="segregate-symbol"
                value={filters.symbol}
                onChange={(event) => updateFilter("symbol", event.target.value)}
                IconComponent={SelectDropdownIcon}
              >
                <MenuItem value="all">All</MenuItem>
                {EXCHANGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-start-date">
              Start Date
            </label>
            <TextField
              id="segregate-start-date"
              size="small"
              fullWidth
              placeholder="DD/MM/YYYY"
              value={filters.startDate}
              onChange={(event) => updateFilter("startDate", event.target.value)}
              className={styles["field-input"]}
            />
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-expiry-date">
              Expiry Date
            </label>
            <TextField
              id="segregate-expiry-date"
              size="small"
              fullWidth
              placeholder="DD/MM/YYYY"
              value={filters.expiryDate}
              onChange={(event) => updateFilter("expiryDate", event.target.value)}
              className={styles["field-input"]}
            />
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-instrument-type">
              Instrument Type
            </label>
            <FormControl size="small" fullWidth className={styles["field-input"]}>
              <Select
                id="segregate-instrument-type"
                value={filters.instrumentType}
                onChange={(event) => updateFilter("instrumentType", event.target.value)}
                IconComponent={SelectDropdownIcon}
              >
                <MenuItem value="all">All</MenuItem>
                {INSTRUMENT_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-broker-code">
              BrokerCode Selection
            </label>
            <FormControl size="small" fullWidth className={styles["field-input"]}>
              <Select
                id="segregate-broker-code"
                value={filters.brokerCode}
                onChange={(event) => updateFilter("brokerCode", event.target.value)}
                IconComponent={SelectDropdownIcon}
              >
                <MenuItem value="all">All</MenuItem>
                {EXCHANGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-strategy">
              Strategy Selection
            </label>
            <FormControl size="small" fullWidth className={styles["field-input"]}>
              <Select
                id="segregate-strategy"
                displayEmpty
                value={filters.strategy}
                onChange={(event) => updateFilter("strategy", event.target.value)}
                IconComponent={SelectDropdownIcon}
                renderValue={(selected) => selected || "Select Basket"}
              >
                <MenuItem value="">Select Basket</MenuItem>
                {strategyOptions.map((strategy) => (
                  <MenuItem key={strategy.id || strategy.strategy_id} value={strategy.strategy_name}>
                    {strategy.strategy_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-strike">
              Select Strike
            </label>
            <FormControl size="small" fullWidth className={styles["field-input"]}>
              <Select
                id="segregate-strike"
                displayEmpty
                value={filters.strike}
                onChange={(event) => updateFilter("strike", event.target.value)}
                IconComponent={SelectDropdownIcon}
                renderValue={(selected) => selected || "Select Basket"}
              >
                <MenuItem value="">Select Basket</MenuItem>
                <MenuItem value="22600">22600</MenuItem>
                <MenuItem value="22700">22700</MenuItem>
                <MenuItem value="22800">22800</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className={styles["field-group"]}>
            <label className={styles["field-label"]} htmlFor="segregate-batch-id">
              Batch ID
            </label>
            <TextField
              id="segregate-batch-id"
              size="small"
              fullWidth
              placeholder="Batch ID"
              value={filters.batchId}
              onChange={(event) => updateFilter("batchId", event.target.value)}
              className={styles["field-input"]}
            />
          </div>
        </div>

        <div className={styles["buy-sell-group"]}>
          <button
            type="button"
            className={`${styles["buy-sell-btn"]} ${
              transactionType === "Buy" ? styles["buy-sell-btn-buy-active"] : ""
            }`}
            onClick={() => setTransactionType("Buy")}
          >
            Buy
          </button>
          <button
            type="button"
            className={`${styles["buy-sell-btn"]} ${
              transactionType === "Sell" ? styles["buy-sell-btn-sell-active"] : ""
            }`}
            onClick={() => setTransactionType("Sell")}
          >
            Sell
          </button>
        </div>

        <div className={styles["table-wrapper"]}>
          {isLoadingTrades ? (
            <div className={styles["loading-message"]}>Loading trades...</div>
          ) : filteredTrades.length === 0 ? (
            <div className={styles["empty-message"]}>No trades found</div>
          ) : (
            <table className={styles["trades-table"]}>
              <thead>
                <tr>
                  <th className={styles["checkbox-cell"]}>
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={!allVisibleSelected && someVisibleSelected}
                      onChange={handleToggleAll}
                    />
                  </th>
                  <th>Symbol</th>
                  <th>Client ID</th>
                  <th>QTY/Lots</th>
                  <th>Transitions</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrades.map((trade) => (
                  <tr key={trade.id}>
                    <td className={styles["checkbox-cell"]}>
                      <Checkbox
                        size="small"
                        checked={selectedTradeIds.includes(trade.id)}
                        onChange={() => handleToggleRow(trade.id)}
                      />
                    </td>
                    <td>{trade.symbol}</td>
                    <td>{trade.clientId}</td>
                    <td>{trade.qtyLots}</td>
                    <td>{trade.transitions}</td>
                    <td>{trade.timestamp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {error ? (
          <Alert severity="error" className={styles["error-alert"]}>
            {error}
          </Alert>
        ) : null}

        <div className={styles["modal-actions"]}>
          <Button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className={styles["cancel-btn"]}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedTradeIds.length === 0}
            className={styles["submit-btn"]}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmitting ? "Segregating…" : "Segregate Trades"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default SegregateTradesModal;
